import React, { useEffect, useRef, useState, createContext, useImperativeHandle, useMemo } from "react";
import { ReactGrid } from "@codametrix/grid"; // the AG Grid React Component
import {
  QUEUE_SUBVIEW_CONFIG,
  DEFAULT_SUBVIEW_ID,
  getSubviewFilters,
  isQueueView,
} from './constants';
import {
  Box,
  Button,
  Input,
  IconButton,
  Typography,
  Badge,
  Tooltip,
  debounce,
  Grid, Popover, Divider,
  Tab
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import filter from "../../../../public/assets/icons/filter.svg";
import cancel from "../../../../public/assets/icons/cancel.svg";
import code from "../../../../public/assets/icons/code.svg";
import hidden from "../../../../public/assets/icons/hidden.svg";
import refresh from "../../../../public/assets/icons/refresh.svg";
import search from "../../../../public/assets/icons/search.svg";
import add from "../../../../public/assets/icons/add.svg";
import reset from "../../../../public/assets/icons/reset.svg";
import check from "../../../../public/assets/icons/check.svg";
import info from "../../../../public/assets/icons/info.svg";
import verticalMenu from "../../../../public/assets/icons/verticalMenu.svg";
import resetViewIcon from "../../../../public/assets/icons/reset_view.svg";
import theme from "../../../theme/Theme";
import { OptionsMenu } from "../../molecules/OptionsMenu";
import { directoryById } from "../../../queries/DirectoryData";
import { SearchWorkItemEntries } from "../../../queries/searchWorkItemsData";
import { UpdateColumnName } from "../../../queries/updateAttributeLabel";
import { BulkUpdateWorkItems } from "../../../queries/BulkUpdateWorkItems";
import { BulkExcludeWorkItems } from "../../../queries/BulkExcludeWorkItems";
import { Coders } from "../../../queries/Workers";
import { getViews, createView, updateView, deleteUserView } from "../../../queries/views";
import TabList from "@mui/lab/TabList";
import { AddViewPopup } from "../../molecules/AddViewPopup";
import { ActionMenu } from "../../molecules/ActionMenu";
import { SaveUserPreferences } from "../../../queries/SaveUserPreferences";
import {
  collectionNamesQuery,
  generateKey,
  policiesContext,
  reverseServiceLineMapper,
  safeJsonStringify,
  serviceLineMapper,
  USER_ROLES,
  WidgetConstants,
} from "../../../utils/common";
import { transformSearchFieldsToFilters, updatedWorklistColumnDefs } from "../../../utils/columnMapper";
import "./index.css";
import { apiGetBulkCoderNotes, apiReassignCQAWorkitems, fetchRiskCategories, utilGetGroups } from "../../../utils/api";
import { QueryGroupUserRoles } from "../../../queries/GroupUserRoles";
import { SnackbarContext } from "../../../context/AlertsContext";
import CoderNotesRowItem from "../../atoms/CoderNotesRowItem";
import { Filters, defaultColumnOptions } from "../../molecules/Filters";
import { SearchWorkItemsResponse } from "../../../types/sd";
import { cqaTransformWorkItems } from "../../../utils/cqa/transform";
import { CQAChangesRow } from "../../molecules/QualityAssessmentRows/CQAChangesRow";
import { ICellRendererParams } from "@codametrix/grid";
import { RefreshCaseCache } from "../../../queries/CursorNavigation";
import CQACodersCell from "../../molecules/QualityAssessmentRows/CQACodersCell";
import { CollectionNames } from "../../../queries/profileData";
import { useWorklistConfig } from '../../../hooks/useWorklistConfig';
import { SmartSearchField } from '../../molecules/SmartSearchField';
import { toMs, formatDate, epochComparator } from "../../../utils/cqa/dosUtil";
import StatusTabs from "../../molecules/StatusTabs";
import { WorkitemStatus } from "../../molecules/ProjectSelectColumn/types";


//region Types & Interfaces
type Props = {
  client: any;
  setShowDetails?: React.Dispatch<React.SetStateAction<boolean>>;
  DirectoryType?: string;

  bulkActionsDisabled?: boolean;
  cqaWorkitemStatus?: string;

  widgetHeader?: React.ReactNode;
  widgetType: string;
  widgetTitle: string | React.ReactNode;
  widgetTitleActions?: React.ReactNode;

  onTableRefresh?: Function;


  handleRowClick?: Function;
  childRef?: React.RefObject<unknown>;

  isQualityAssessment?: boolean;
  handleViewWorkitemData?: Function;

  overridingViewFilters?: FiltersType | [];
  groupByCQAWorkItemId?: boolean;
  GraderCountsRow?: React.ReactNode;
  shouldLoadData?: boolean;
  projectIdentifier?: string;
  hideTabNavigation?: boolean;
  externalActiveView?: ViewType;
  onViewsLoaded?: (views: ViewType[]) => void;
  onActiveViewChange?: (view: ViewType) => void;
  selectedProject?: any;
  selectedStatus?: WorkitemStatus | null;
  availableStatuses?: WorkitemStatus[];
  onStatusChange?: (status: WorkitemStatus | null) => void;
  countIsLoading?: boolean;
};

interface SortOrder {
  fieldName: string;
  order: "ASC" | "DESC";
}

export interface ViewType {
  viewId?: string;
  name: string;
  filters: FiltersType | [];
  columns?: any[];
  userFilters?: any[];
  sort_order?: SortOrder;
  description: string;
  roles?: any[];
  view_type?: string;
  editable?: boolean;
  createdby?: String;
  userPreferences?: {
    position: number;
    isHidden: boolean;
  };
  widgetType?: any;
  scope?: string;
}

interface FiltersType {
  combinator?: "AND" | "OR";
  filters?: FiltersType[];
}
//endregion

//region Functions
const constValueGetter = () => {
  return "";
}

const buildFilterValue = (value, operator) => {
  switch (operator) {
    case "like":
      return `%${value}%`;
    case "startsWith":
      return `${value}%`;
    case "endsWith":
      return `%${value}`;
    default:
      return value;
  }
}

export const caseContext = createContext({
  directoryType: "",
  columnOptions: [],
  detailGridOptions: {},
  optionsDict: {},
  setOptionsDict: (dict: any) => { },
});

export const recursiveBuildSchema = (
  parentModel: any,
  relationshipDict: any,
  modelDict: any,
  mappingName?: any
) => {
  const obj: any = {};
  const columnFields = [];
  parentModel?.attributes?.map((attribute) => {
    if (!attribute?.systemOnly) {
      columnFields.push({
        id: attribute?.id,
        field: attribute.name,
        headerName: attribute.label,
        description: attribute.description,
        sortable: true,
        width: 200,
        entityType: parentModel?.typeName,
      });
    }
  });
  if (!relationshipDict?.[parentModel?.id]) {
    obj["detailGridOptions"] = {
      columnDefs: columnFields,
      masterDetail: false,
      cacheBlockSize: PAGE_SIZE,
      defaultColDef: {
        flex: 1,
        sortable: true,
        width: 500,
        resizable: true,
      },
    };

    obj["getDetailRowData"] = function (params) {
      return params.successCallback(params?.data?.[mappingName]);
    };
    return obj;
  } else {
    const childValuesOfParent = relationshipDict?.[parentModel?.id];
    let detailParams;
    childValuesOfParent?.map((childValue) => {
      detailParams = recursiveBuildSchema(
        modelDict?.[childValue?.childId],
        relationshipDict,
        modelDict,
        childValue?.mappingName
      );
      columnFields.push({
        field: childValue?.mappingName,
        headerName: childValue?.mappingName,
        sortable: true,
        width: 200,
        cellRenderer: "agGroupCellRenderer",
        valueGetter: constValueGetter,
      });
    });
    const getRowData = parentModel?.root
      ? undefined
      : function (params) {
        return params.successCallback(params?.data?.[mappingName]);
      };
    obj["detailGridOptions"] = {
      columnDefs: columnFields,
      masterDetail: true,
      detailRowAutoHeight: true,
      detailCellRendererParams: detailParams,
      defaultColDef: {
        flex: 1,
        sortable: true,
        width: 500,
        resizable: true,
      },
    };
    obj["getDetailRowData"] = getRowData;

    return obj;
  }
};

/**
 * Checks whether a filter value is meaningful and should be included.
 * Excludes empty, null, or structurally empty values (e.g. [], [[]]).
 */
export const hasValidValue = (value: any): boolean => {
  if (value === undefined) return false;

  if (value === null) return true;

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.some(
      v =>
        v != null &&
        (!Array.isArray(v) || v.length > 0)
    );
  }

  return true;
};

/**
 * Builds a normalized search filter object from UI filters.
 * Ignores filters with invalid values and safely combines valid ones.
 */

export const buildSearchFilter = (filtersData) => {
  let obj;

  const validFilters = filtersData?.filter(filter =>
    hasValidValue(filter?.value)
  );

  validFilters?.forEach((filter: any, index: number) => {
    const temp = {
      key: filter.fieldName,
      operation: filter.operator,
      value: buildFilterValue(filter?.value, filter?.operator ?? ""),
    };

    // First valid filter always initializes obj
    if (index === 0) {
      obj = { ...temp };
      return;
    }

    obj = {
      filters: [{ ...obj }, { ...temp }],
      combinator: filter.join ?? 'AND',
    };
  });

  if (!validFilters?.length) {
    return { filters: [] };
  }

  if (validFilters.length === 1) {
    return { filters: [obj] };
  }

  return obj;
};

export const buildColumnsDict = (detailGridOptions) => {
  let columnNames = [];
  detailGridOptions?.columnDefs?.map((columnDef) => {
    if (!columnDef?.cellRenderer) {
      columnNames.push(columnDef?.id);
    }
  });
  if (detailGridOptions?.masterDetail) {
    const l = buildColumnsDict(
      detailGridOptions?.detailCellRendererParams?.detailGridOptions
    );
    columnNames = [...columnNames, ...l];
  }
  return columnNames;
};
//endregion

//region Constants
const PAGE_SIZE = 20;
const GROUPED_BY_ENCOUNTER = "Grouped by encounter";
const NOT_GROUPED_BY_ENCOUNTER = "Not grouped by encounter";
const RESET_DEFAULT_SORT = "Reset to default sort";
const DEFAULT_SORT_FIELD = "date_of_service";
const DEFAULT_SORT_ORDER = "ASC";
const ALL_SELECTED = "All selected";
const ROLE_CODING_MANAGER = "coding_manager";

export const managerViews = Object.freeze([
  "Current",
  "Recent",
  "Manager Review",
  "QA",
  "On Hold"
]);

const cqaManagerViews = Object.freeze([
  "My cases to assess",
  "All cases to assess"
]);

const codingQualityViews = Object.freeze([
  "My cases to review",
  "All cases to review"
]);

export const coderRedistributionViews = Object.freeze([
  "My Current",
  "My Holds"
]);

const shouldShowSelectAll = (viewName: string | undefined, roles: string[]): boolean => {
  // Show "select all" column for all views (except Coding Quality views),
  // and for CQA views only when the user has the CQA Manager role
  if (!viewName) return false;
  return (
    !codingQualityViews.includes(viewName) &&
    (
      (cqaManagerViews.includes(viewName) && roles.includes("cqa_manager")) ||
      !cqaManagerViews.includes(viewName)
    )
  );
};



const selectColumnDef = {
  id: "checkbox",
  field: "",
  editable: false,
  hide: false,
  checkboxSelection: true,
  headerCheckboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
  width: 50,
  isMeta: true,
  lockPosition: true,
  resizable: false,
  sortable: false,
};

const createCoderNotesColumnDef = (serviceLine) => ({
  id: "coder_notes",
  field: "",
  editable: false,
  hide: false,
  checkboxSelection: false,
  headerCheckboxSelection: false,
  width: 60,
  isMeta: true,
  lockPosition: true,
  sortable: false,
  resizable: false,
  cellRenderer: (params) => {
    const isPathology = serviceLine === 'Pathology';
    return (
      <CoderNotesRowItem
        coderNotes={params.data.coder_notes}
        patientMrn={!isPathology ? params.data.patient_mrn : undefined}
        accession={isPathology ? params.data.accession : undefined}
      />
    );
  }
});

const workitemDetailColumnDef = {
  id: "workitem_detail",
  field: "workitem_detail",
  headerName: "Workqueue JSON",
  editable: false,
  hide: false,
  checkboxSelection: false,
  headerCheckboxSelection: false,
  width: 150,
  isMeta: true,
  lockPosition: true,
  sortable: false,
  resizable: false,
  suppressRowClickSelection: true,
  cellRenderer: (params) => {
    return (
      <Tooltip title="Workqueue JSON" arrow>
        <IconButton disableRipple={true}>
          <img src={code} alt="workqueue json"  style={{ height: "14px", filter: "invert(71%) sepia(14%) saturate(1622%) hue-rotate(324deg) brightness(97%) contrast(89%)" }} />
        </IconButton>
      </Tooltip>
    );
  }
};

// TODO: create pathology views and remove this column
const accessionColumnDef = {
  id: "accession",
  field: "accession",
  headerName: "Accession",
  editable: false,
  hide: false,
  checkboxSelection: false,
  headerCheckboxSelection: false,
  width: 150,
  suppressRowClickSelection: true,
  sortable: true,
  resizable: true,
};

const sortDisabledColumns = ['profile', 'billing_area', 'assignee', 'owner']


export const defaultColumns = ['checkbox', 'coder_notes', 'workitem_detail'];
//endregion

const WorkItemTable = ({
  DirectoryType = "workitem",
  handleRowClick = () => { /* empty */ },
  setShowDetails = () => { /* empty */ },
  ...props
}: Props) => {

  //region Context
  const { openSnackbar } = React.useContext(SnackbarContext);
  //endregion

  const { config: worklistConfig, loading: configLoading, error: configError } = useWorklistConfig(props.isQualityAssessment);

  //region State
  const [gridApi, setGridApi] = useState<any>(null);
  const [nameSearch, setNameSearch] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [addviewAnchorEl, setAddViewAnchorEl] = useState(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [filters, setFilters] = React.useState<any>([]);  
  const [hideFieldsAnchorEl, setHideFieldsAnchorEl] = useState(null);
  const [views, setViewsState] = useState<ViewType[]>([
    { viewId: "1", name: "Current", filters: [], columns: [], description: "" },
  ]);
  const [activeView, setActiveView] = React.useState<ViewType>();
  const [columnDefs, setColumnDefs] = React.useState<any>([]);
  const [columnData, setColumnData] = React.useState<any>([]);
  const [hiddenNumberOfFields, setHiddenNumberOfFields] =
    React.useState<number>(0);
  const [filterApplied, setFilterApplied] = React.useState<boolean>(false);
  const [detailCellRendererParams, setDetailCellRendererParams] =
    React.useState<any>({});
  const [detailGridOptions, setDetailGridOptions] = React.useState({});
  var totalCount = PAGE_SIZE;
  var searchCount = PAGE_SIZE;
  const [entries, setEntries] = useState({});
  const [numberOfFilters, setNumberOfFilters] = React.useState<number>(0);
  const [countOfEntries, setCountOfEntries] = React.useState<number>(0);
  const [optionsDict, setOptionsDict] = React.useState({});
  const [showCount, setShowCount] = useState<boolean>(false);
  const [searchAttributeName, setSearchAttributeName] = useState("");
  const [rootIdentifierColumn, setRootIdentifierColumn] = useState<String>("");
  const [allFilters, setAllFilters] = React.useState<any>({});

  const [searchType, setSearchType] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<Array<{key: string, label: string}>>([]);
  const [activeSubviewId, setActiveSubviewId] = useState<string | null>(null);

  const { roles, userContext, coderAssignmentConfig } = React.useContext(policiesContext);
  const isCQAManager = roles.includes("cqa_manager");
  const availableStatusesForTabs = props.availableStatuses ?? [];
  const shouldShowStatusTabs =
    !!props.isQualityAssessment &&
    !!props.selectedProject &&
    availableStatusesForTabs.length > 0;
  const statusTabsLabelOverrides: Partial<Record<WorkitemStatus, string>> = isCQAManager
    ? {
        [WorkitemStatus.IN_CQA]: "Remaining Cases",
        [WorkitemStatus.CQA_COMPLETE]: "All Completed",
        [WorkitemStatus.CQA_APPROVED]: "Approved",
      }
    : {};
  const workItemCounts = props.selectedProject?.workItemCounts || {};
  const remainingCount = props.countIsLoading ? undefined : workItemCounts[WorkitemStatus.IN_CQA] ?? 0;
  const completedCount = props.countIsLoading ? undefined : workItemCounts[WorkitemStatus.CQA_COMPLETE] ?? 0;
  const showRemainingEmptyState =
    isCQAManager &&
    props.isQualityAssessment &&
    props.selectedStatus === WorkitemStatus.IN_CQA &&
    remainingCount === 0;
  const showCompletedEmptyState =
    isCQAManager &&
    props.isQualityAssessment &&
    props.selectedStatus === WorkitemStatus.CQA_COMPLETE &&
    completedCount === 0;
  const showManagerEmptyState = showRemainingEmptyState || showCompletedEmptyState;
  const emptyStateContent = showRemainingEmptyState
    ? {
        title: "Yay! Your project has been completed!",
        description:
          "All cases in this data set have been evaluated and enriched. Please look at the completed view to approve the cases for data retention.",
      }
    : showCompletedEmptyState
    ? {
        title: "Yay! You have approved all data sets in this project!",
        description:
          "All cases in this data set have been evaluated, enriched and approved.",
      }
    : null;

  const isGroupByEncounter = useMemo(
    () =>
      coderAssignmentConfig?.some(
        (item) =>
          item.keyname === "patient_grouping" &&
          item.values.some(
            (valueObj) =>
              valueObj.value === "true" &&
              valueObj.serviceLines.includes(
                reverseServiceLineMapper[userContext?.serviceLine?.name] ??
                  userContext?.serviceLine?.name
              )
          )
      ),
    [coderAssignmentConfig]
  );

  const groupByEncounterRef = useRef((props.isQualityAssessment || !isGroupByEncounter) ? false : true);
  const [sortColumn, setSortColumn] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [availableCoders, setAvailableCoders] = useState([]);
  const [allCoders, setAllCoders] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [queues, setQueues] = useState([]);
  const [userHasGroups, setUserHasGroups] = useState(true); // if user is part of any groups
  const [groupsChecked, setGroupsChecked] = useState(false);
  const [projectRetrievalError, setProjectRetrievalError] = useState(false); // if there was an error retrieving projects
  //endregion

  //region Helper Functions
  const setViews = (views: ViewType[]) => {
    setViewsState(views);
    if (props.onViewsLoaded) {
      props.onViewsLoaded(views);
    }
  };

  const hideColumns = (option) => {
    const cols = columnDefs?.map((column) => {
      if (column.field === option.field) {
        if (column.hide) {
          setHiddenNumberOfFields(hiddenNumberOfFields - 1);
        } else {
          setHiddenNumberOfFields(hiddenNumberOfFields + 1);
        }
        return { ...column, hide: !column.hide };
      }
      return column;
    })
    setColumnDefs(cols);

    const tempViews: ViewType[] = [...views];
    const viewIdx = tempViews?.findIndex(
      (view) => view?.viewId === activeView?.viewId
    );
    tempViews[viewIdx] = { ...tempViews[viewIdx], columns: cols };

    setViews(tempViews);
    saveUserPreferences({
      columns: cols,
      filters: activeView?.filters,
      userFilters: activeView?.userFilters,
    });
  };

  const handleViewChange = (view: ViewType) => {
    gridApi && gridApi.deselectAll();
    setActiveView(view);
    if (props.hideTabNavigation && isQueueView(view)) {
      setActiveSubviewId(DEFAULT_SUBVIEW_ID);
    } else {
      setActiveSubviewId(null);
    }
    setFilters(view?.userFilters ?? []);
    setNumberOfFilters(view?.userFilters?.length ?? 0);
    setFilterApplied(view?.userFilters ? true : false);
    setShowCount(false);
    searchCount = PAGE_SIZE;
    if (props.onActiveViewChange) {
      props.onActiveViewChange(view);
    }
  };

  const handleSubviewChange = (subviewId: string) => {
    if (subviewId === activeSubviewId) return;
    setActiveSubviewId(subviewId);
  };

  const hideAllFields = () => {
    const cols = columnDefs?.map((column) => {
      if (!defaultColumns.includes(column.id)) {
        return { ...column, hide: true };
      }
      return column;
    });

    setColumnDefs(cols);
    setHiddenNumberOfFields(cols?.length);

    const tempViews: ViewType[] = [...views];
    const viewIdx = tempViews?.findIndex(
      (view) => view?.viewId === activeView?.viewId
    );
    tempViews[viewIdx] = { ...tempViews[viewIdx], columns: cols };

    setViews(tempViews);
    saveUserPreferences({
      columns: cols,
      filters: activeView?.filters,
      userFilters: activeView?.userFilters,
    });
  };

  const showAllFields = () => {
    const cols = columnDefs?.map((column) => {
      return { ...column, hide: false };
    });
    setColumnDefs(cols);
    setHiddenNumberOfFields(0);

    const tempViews: ViewType[] = [...views];
    const viewIdx = tempViews?.findIndex(
      (view) => view?.viewId === activeView?.viewId
    );
    tempViews[viewIdx] = { ...tempViews[viewIdx], columns: cols };

    setViews(tempViews);
    saveUserPreferences({
      columns: cols,
      filters: activeView?.filters,
      userFilters: activeView?.userFilters,
    });
  };

  const saveView = (
    config: any,
    name: string,
    description: string,
    scope: string,
    view_type: string,
    isEdit: boolean,
    viewId?: any,
    roles?: any[],
    service?: string
  ) => {
    const mutation = isEdit ? updateView : createView;

    let variables: { view: any, updateViewId?: string } = {
      view: {
        widgetType: props.widgetType,
        config,
        name,
        scope,
        description,
        roles: view_type === 'global' ? ['*'] : roles,
        view_type,
        service,
      }
    };

    if (isEdit) {
      variables = {
        ...variables,
        updateViewId: viewId,
      };
      delete variables.view['roles'];
    }

    props?.client
      .mutate({
        mutation,
        variables,
        fetchPolicy: "no-cache",
      })
      .then(async (res) => {
        const tempViews: ViewType[] = views.filter(view => view.viewId !== viewId);
        const resViewId = isEdit ? res?.data?.UpdateView?.data?.view_id : res?.data?.CreateView?.data?.view_id;
        const viewObj: ViewType = {
          viewId: resViewId,
          name,
          description,
          filters: config.filters,
          columns: buildColumnDefs(config.name, config.columns),
          sort_order: config.sort_order,
          view_type: view_type,
          editable: true,
          createdby: userContext?.user?.userId || '',
        };

        tempViews.push(viewObj);
        setViews(tempViews);
        handleViewChange(viewObj);
      })
      .catch((err) => {
        console.log(err);
        openSnackbar("Saving View Failed", "error");
      });
  };

  const saveUserPreferences = (config: any) => {
    if(activeSubviewId) return;
    const viewToSave = { ...config }
    viewToSave.columns = viewToSave.columns.filter(column => !defaultColumns.includes(column.id));

    props?.client
      .mutate({
        mutation: SaveUserPreferences,
        variables: {
          userView: {
            config: viewToSave,
            view_id: activeView?.viewId,
            service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
          },
        },
        fetchPolicy: "no-cache",
      })
      .then(async (res) => {
        // getWidgetPreferences();
      })
      .catch((err) => {
        console.log(err);
        openSnackbar("Saving User Preferences Failed", "error");
      });
  };

  const ColumDataCall = (data) => {
    let columnData = [];
    var Columns = data;
    const dict = entries;
    Columns?.forEach((column) => {
      const cellData = column.data;
      if (cellData) {
        Object.keys(cellData)?.map((key) => {
          dict?.[key]?.add(cellData?.[key]);
        });
        columnData.push(cellData);
      }
    });
    setEntries(dict);
    return columnData;
  };

  const handleSmartSearch = (newSearchType: string, newSearchValue: string) => {
    setSearchType(newSearchType);
    setSearchValue(newSearchValue);
    setNameSearch(""); // Clear legacy search

    let combinationFilters: FiltersType = {};
    const appliedFilters = buildSearchFilter(filters);
    const searchFilter = {
      key: newSearchType,
      operation: "like",
      value: `%${newSearchValue}%`,
    };

    combinationFilters = {
      combinator: "AND",
      filters: appliedFilters?.filters?.length
          ? [searchFilter, appliedFilters]
          : [searchFilter],
    };

    firstSearchRender.current = true;
    setShowCount(false);
    gridApi?.setServerSideDatasource(
        updateServerSideDatasource(combinationFilters, undefined, true)
    );
  };

  const handleSmartSearchClear = () => {
    setSearchValue("");
    setSearchType(worklistConfig?.defaultFilterOption || "");
    setNameSearch("");
    setShowCount(false);
    firstSearchRender.current = true;
    let body = {};
    if (filters?.length) {
      body = buildSearchFilter(filters);
    }
    gridApi?.setServerSideDatasource(
        updateServerSideDatasource(body, undefined, true)
    );
  };

  const getSearchFilter = () => {
    let filterObj;

    if (searchValue?.length) {
      const effectiveSearchType = searchType || worklistConfig?.defaultFilterOption || searchAttributeName || 'patient_mrn';
      filterObj = {
        key: effectiveSearchType,
        operation: "like",
        value: `%${searchValue}%`,
      };
    }
    return filterObj;
  };

  const filterVisibleViews = (views: ViewType[]) => {
    return views.filter((view) => {
      const userPrefs = view.userPreferences;
      const isHidden = userPrefs?.isHidden ?? false;
      return !isHidden;
    });
  };

  const applyFilters = (newFilters) => {
    if (newFilters?.length) {
      transformSearchFieldsToFilters(newFilters, allCoders);
      let combinationFilters: FiltersType = {};
      const searchFilter = getSearchFilter();
      const buildFilters = buildSearchFilter(newFilters);
      if (searchFilter && Object.keys(searchFilter)?.length)
        combinationFilters = {
          combinator: "AND",
          filters: [buildFilters, searchFilter],
        };
      firstSearchRender.current = true;
      setShowCount(false);
      gridApi?.setServerSideDatasource(
        updateServerSideDatasource(
          Object.keys(combinationFilters)?.length
            ? combinationFilters
            : buildFilters,
          undefined,
          true
        )
      );
      const tempViews: ViewType[] = JSON.parse(JSON.stringify(views));
      const viewIdx = tempViews?.findIndex(
        (view) => view?.viewId === activeView?.viewId
      );
      tempViews[viewIdx] = { ...tempViews[viewIdx], userFilters: newFilters };
      setViews(tempViews);
      setFilterApplied(true);
      setNumberOfFilters(newFilters?.length);
      saveUserPreferences({
        columns: columnDefs,
        filters: activeView?.filters,
        userFilters: newFilters,
      });
    }
  };

  const removeFilters = () => {
    setFilters([]);
    setNumberOfFilters(0);
    setFilterApplied(false);
    handleFilterClose();
    setShowCount(false);
    firstSearchRender.current = true;
    let tempFilters = [];
    const searchFilter = getSearchFilter();
    searchFilter && tempFilters.push(searchFilter);
    gridApi?.setServerSideDatasource(
      updateServerSideDatasource(searchFilter ?? {}, undefined, true)
    );
    const tempViews: ViewType[] = JSON.parse(JSON.stringify(views));
    const viewIdx = tempViews?.findIndex(
      (view) => view?.viewId === activeView?.viewId
    );
    tempViews[viewIdx] = { ...tempViews[viewIdx], userFilters: [] };
    setViews(tempViews);
    saveUserPreferences({
      columns: columnDefs,
      filters: activeView?.filters,
    });
  };

  function constValueGetter() {
    return "";
  }

  const recursiveBuildSchema = (
    parentModel: any,
    relationshipDict: any,
    modelDict: any,
    mappingName?: any
  ) => {
    const obj: any = {};
    const columnFields = [];
    parentModel?.attributes?.map((attribute) => {
      if (!attribute?.systemOnly) {
        columnFields.push({
          id: attribute?.id,
          field: attribute.name,
          headerName: attribute.label,
          description: attribute.description,
          sortable: !sortDisabledColumns.includes(attribute.name),
          width: 200,
          entityType: parentModel?.typeName,
          modelName: attribute?.typeName
        });
      }
    });
    if (!relationshipDict?.[parentModel?.id]) {
      obj["detailGridOptions"] = {
        columnDefs: columnFields,
        masterDetail: false,
        cacheBlockSize: PAGE_SIZE,
        defaultColDef: {
          flex: 1,
          sortable: true,
          width: 500,
          resizable: true,
        },
      };

      obj["getDetailRowData"] = function (params) {
        return params.successCallback(params?.data?.[mappingName]);
      };

      return obj;
    } else {
      const childValuesOfParent = relationshipDict?.[parentModel?.id];
      var detailParams;
      childValuesOfParent?.map((childValue) => {
        detailParams = recursiveBuildSchema(
          modelDict?.[childValue?.childId],
          relationshipDict,
          modelDict,
          childValue?.mappingName
        );
        columnFields.push({
          field: childValue?.mappingName,
          headerName: childValue?.mappingName,
          sortable: true,
          width: 200,
          cellRenderer: "agGroupCellRenderer",
          valueGetter: constValueGetter,
        });
      });
      const getRowData = parentModel?.root
        ? undefined
        : function (params) {
          return params.successCallback(params?.data?.[mappingName]);
        };
      obj["detailGridOptions"] = {
        columnDefs: columnFields,
        masterDetail: true,
        detailRowAutoHeight: true,
        detailCellRendererParams: detailParams,
        defaultColDef: {
          flex: 1,
          sortable: true,
          width: 500,
          resizable: true,
        },
      };
      obj["getDetailRowData"] = getRowData;
      return obj;
    }
  };

  const updateViewsInCQA = async (_views: ViewType[]) => {
    if(props.isQualityAssessment){
      const cqaRiskDictionaries = await fetchRiskCategories();
      const cqaChangesView = _views[0].columns.find((_view) => _view.field === 'cqa_changes');
      if(cqaChangesView){
        cqaChangesView.cellRenderer = (context: ICellRendererParams) => CQAChangesRow({ data: context.data, riskDictionaries: cqaRiskDictionaries});
      }
      const cqaCodesView = _views[0].columns.find((_view) => _view.field === 'cqa_codes');
      if(cqaCodesView){
        cqaCodesView.cellRenderer = (context: ICellRendererParams) => CQACodersCell({ data: context.data });
      }
      const dosCol = _views[0].columns.find(c => c.field === 'date_of_service');
      if (dosCol) {
        dosCol.cellRenderer = ({ value, data }: ICellRendererParams) => {
          const raw = value ?? data?.datetime_of_service ?? data?.date_of_service ?? null;
          const ms = toMs(raw);
          if (ms == null) return "N/A";
          return formatDate(ms, data?.tenant_timezone);
        };
        dosCol.comparator = (a: unknown, b: unknown) =>
          (toMs(a) ?? -Infinity) - (toMs(b) ?? -Infinity);
      }
    }
  };

  const fetchViews = async () => {
    setProjectRetrievalError(false);
    await props?.client
        .query({
          query: getViews,
          variables: {
            widgetType: DirectoryType,
            service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
            showAvailableCases: coderAssignmentConfig?.some(
                (item) =>
                    item.keyname === "nightly_distribution" &&
                    item.values.some(
                        (valueObj) =>
                            valueObj.value === "false" &&
                            valueObj.serviceLines.includes(
                                reverseServiceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name
                            )
                    )
            ),
          },
          fetchPolicy: "no-cache",
        })
        .then(async (response) => {
          const viewsArray = response?.data?.Views?.data;
          if (viewsArray?.length) {
            const views: ViewType[] = [
              ...viewsArray
                  ?.map(mapViewDataToViewType) // Use the extracted function
                  ?.filter((view) =>
                      view?.name
                      && view?.columns
                      && view.widgetType === props.widgetType
                  ),
            ];

            await updateViewsInCQA(views);
            setViews(views);
            handleViewChange(filterVisibleViews(views)?.[0]);
            firstSearchRender.current = true;
          } else {
            setFilters([]);
            setNumberOfFilters(0);
            setFilterApplied(false);
            setColumnDefs([]);
            firstRender.current = true;
            setShowCount(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching views:", error);
          setProjectRetrievalError(true);
          openSnackbar("Get Views Failed", "error");
        });
  }

  const onGridReady = async (params: any) => {
    setGridApi(params.api);
    await fetchViews();
    params.api.sizeColumnsToFit();
  };
  const onGridPreDestroyed = () => {
    setGridApi(null);
  }
  const getAllGroups = async () => {
    const groups = await utilGetGroups(props?.client);
    setAllGroups(groups?.map(group => ({ label: group?.data?.name, value: group?.data?.group_id })));
  }

  const checkUserGroups = async () => {
    // Only proceed if we have the required user context
    if (!userContext?.user?.userId) {
      setGroupsChecked(true);
      return;
    }

    try {
      setGroupsChecked(false);
      const response = await props?.client.mutate({
        mutation: QueryGroupUserRoles,
        variables: {
          groupUserRolesQuery: {
            query: `Select * from group_user_roles where user_id='${userContext.user.userId}';`
          }
        }
      });

      const userGroups = response?.data?.QueryGroupUserRoles;
      const hasGroups = Array.isArray(userGroups) && userGroups.length > 0;
      setUserHasGroups(hasGroups);
      setGroupsChecked(true);
    } catch (error) {
      console.error("Error checking user groups:", error);
      // Treat errors as no-groups so users get an actionable banner
      setUserHasGroups(false);
      setGroupsChecked(true);
    }
  }

  const getCoders = async () => {
    const variables = { pageNumber: 0, pageSize: 100 };
    await props?.client
      .query({
        query: Coders,
        variables: variables,
        fetchPolicy: "no-cache",
      })
      .then((response: any) => {
        const coders = response.data.Coders.data.sort((a, b) => a?.data?.last_name.localeCompare(b?.data?.last_name))
        setAvailableCoders(coders?.filter(coder => coder?.data?.availability?.toUpperCase() === "AVAILABLE"));
        setAllCoders(coders);
      })
      .catch((error: any) => {
        openSnackbar("Failed to Load Coders", "error");
      });
  };

  const getQueues = async () => {
    const profileQuery = collectionNamesQuery(userContext?.serviceLine?.name ,userContext?.activeContext?.healthSystemTenantId)
    
    await props?.client
      .query({
        query: CollectionNames,
        variables: {
          profileQuery: profileQuery,
        },
        fetchPolicy: "no-cache",
      })
      .then((response: any) => {
        const queues = response.data.QueryProfile?.sort((a, b) => a?.name.localeCompare(b?.name))?.map(queue => ({ label: queue?.name, value: queue?.value }));
        setQueues(queues);
      })
      .catch((error: any) => {
        openSnackbar("Failed to Load Queues", "error");
      });
  };

  const firstSearchRender = useRef(true);

  const firstUpdateRender = useRef(true);

  const viewReset = useRef(false);

  const allColumnDefs = useRef<any>({});

  const retryCount = useRef(0);

  const abortControllerRef = useRef<AbortController>(new AbortController());
  const currentProjectRef = useRef<string | undefined>(props.projectIdentifier);

  const appendModelNameToFilterKey = (filters) => {
    if (!filters || !Array.isArray(filters)) {
      return;
    }

    filters.forEach(filter => {
      if (filter.filters) {
        // If the filter has nested filters, recursively process them
        appendModelNameToFilterKey(filter.filters);
      }

      if (filter.key && !filter?.key?.includes('.')) {
        const modelName = allColumnDefs?.current?.find(col => filter?.key === col?.field)?.modelName;
        //  append modelName to each filter key
        if (modelName) filter.key = `${modelName}.${filter.key}`;
      }
    });
  }

  const getBulkNotes = async (workitemsToGetNotes, params) => {
    let identifierType;
    let identifiers;
    if(userContext?.serviceLine?.name === 'Pathology') {
      identifierType = 'accession_numbers';
      identifiers = workitemsToGetNotes.map(workitem => workitem?.data.accession);
    } else {
      identifierType = 'patient_mrns';
      identifiers = workitemsToGetNotes.map(workitem => workitem?.data.patient_mrn);
    }
    const coderNotes = await apiGetBulkCoderNotes(identifiers, identifierType);
    if (!coderNotes) return;

    // Group notes by the appropriate identifier (accession or patient_mrn)
    const groupingKey = userContext?.serviceLine?.name === 'Pathology' ? 'accession_number' : 'patient_mrn';

    const groupedNotes = coderNotes.reduce((acc, note) => {
      const key = note[groupingKey];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(note);
      return acc;
    }, {});

    const newRowData = workitemsToGetNotes.map((row) => {
      const identifier = userContext?.serviceLine?.name === 'Pathology' ? 
        row.data.accession : row.data.patient_mrn;
      const notesForIdentifier = groupedNotes[identifier];
      return { ...row.data, coder_notes: notesForIdentifier };
    });

    setColumnData(newRowData);
    params.success({
      rowData: newRowData,
    });
  }

  function updateServerSideDatasource(
    filterBody: any,
    afterLogin?: boolean,
    sendRowCount?: boolean
  ) {
    return {
      getRows: async function (params: {
        request: any;
        api: any;
        success: (params: any) => {};
        fail: () => void;
      }) {
        var order;
        var fieldName;
        if (params?.request?.sortModel?.length) {
          order = params?.request?.sortModel?.[0]?.sort?.toUpperCase();
          fieldName = params?.request?.sortModel?.[0]?.colId;
        }
        if (afterLogin && firstUpdateRender.current) {
          let variables: any;
          variables = { DirectoryType: DirectoryType };
          await props?.client
            .query({
              query: directoryById,
              variables: variables,
              fetchPolicy: "no-cache",
            })
            .then((response: any) => {
              const relationshipDict = {};
              const searchKey = response?.data?.DirectoryByType?.searchKey;
              if (searchKey) {
                const searchModel =
                  response?.data?.DirectoryByType?.schema?.models?.find(
                    (tempModel) => tempModel?.id === searchKey?.modelId
                  );
                if (searchModel) {
                  const searchAttribute = searchModel?.attributes?.find(
                    (attr) => attr?.id === searchKey?.attributeId
                  );
                  setSearchAttributeName(searchAttribute?.name);
                }
              }
              response?.data?.DirectoryByType?.schema?.relationships?.map(
                (relationship) => {
                  if (relationshipDict[relationship?.srcId]) {
                    relationshipDict[relationship?.srcId]?.push({
                      childId: relationship?.destnId,
                      mappingName: relationship?.name,
                    });
                  } else {
                    relationshipDict[relationship?.srcId] = [
                      {
                        childId: relationship?.destnId,
                        mappingName: relationship?.name,
                      },
                    ];
                  }
                }
              );
              const modelDict = {};
              var rootModel;
              response?.data?.DirectoryByType?.schema?.models?.map((model) => {
                modelDict[model?.id] = model;
                if (model?.root) {
                  rootModel = model;
                }
              });
              const rootIdentifierName = rootModel?.attributes?.find(
                (tempAttribute) => tempAttribute?.id === rootModel?.identifier
              )?.name;
              setRootIdentifierColumn(rootIdentifierName);

              const cellParams = recursiveBuildSchema(
                rootModel,
                relationshipDict,
                modelDict
              );
              const gridOpts = {
                ...cellParams?.detailGridOptions,
                columnDefs: [
                  ...cellParams?.detailGridOptions?.columnDefs,
                  ...response?.data?.DirectoryByType?.factKeys,
                ].sort((a, b) => {
                  let fa = a.headerName?.toLowerCase(),
                    fb = b.headerName?.toLowerCase();

                  if (fa < fb) {
                    return -1;
                  }
                  if (fa > fb) {
                    return 1;
                  }
                  return 0;
                }),
              }

              setDetailGridOptions(gridOpts);
              // Prepend defaultColumnOptions entries that declare a modelName so they
              // take priority in appendModelNameToFilterKey's .find() lookup. This
              // allows filter-only columns (e.g. case_id, which is systemOnly in the
              // schema) to specify which table they belong to.
              const filterModelOverrides = defaultColumnOptions
                .filter(col => col.modelName)
                .map(({ field, modelName }) => ({ field, modelName }));
              allColumnDefs.current = [
                ...filterModelOverrides,
                ...gridOpts?.columnDefs,
              ];
              setDetailCellRendererParams(
                cellParams?.detailGridOptions?.detailCellRendererParams
              );
              const columnNames = buildColumnsDict(
                cellParams?.detailGridOptions
              );

              const dict = {};
              columnNames?.map((columnName) => {
                dict[columnName] = [];
              });
              setOptionsDict(dict);
              firstRender.current = false;
              firstUpdateRender.current = false;
            })
            .catch((error: any) => {
              params.fail();
              console.log(error, "error");
              retryCount.current = retryCount.current + 1;
              if (retryCount.current <= 3) firstUpdateRender.current = false;
              openSnackbar("Failed to Load Directory Metadata", "error");
            });
        }
        if (searchCount >= params.request.startRow || viewReset.current) {
          let newPageSize = PAGE_SIZE;
          if (searchCount - params.request.startRow < newPageSize) {
            newPageSize = searchCount - params.request.startRow;
          }
          params.request.endRow = params.request.startRow + newPageSize;
          const pageNumber = params.request.startRow / PAGE_SIZE + 1;

          let filters = JSON.parse(safeJsonStringify(activeView?.filters));

          // Stitch subview filters for queue views
          const subviewFilters = getSubviewFilters(activeSubviewId);
          if (subviewFilters.length > 0) {
            if (filters?.length || filters?.filters?.length) {
              filters = [
                ...(Array.isArray(filters) ? filters : [filters]),
                ...subviewFilters,
              ];
            } else {
              filters = [...subviewFilters];
            }
          }

          // Rather than using view in the workitems table, we can pass in custom view filters
          if (props.overridingViewFilters) {
            filters = props.overridingViewFilters;
          }
          
          let combinationFilters: FiltersType = {};
          if (filters?.length || filters?.filters?.length) {
            if (Array.isArray(filters)) {
              combinationFilters = {
                combinator: "AND",
                filters: [
                  ...filters,
                  filterBody?.filters?.length ? filterBody : null,
                ],
              };
            } else {
              combinationFilters = {
                combinator: "AND",
                filters: [
                  filters,
                  filterBody?.filters?.length ? filterBody : null,
                ],
              };
            }
          } else {
            combinationFilters = JSON.parse(safeJsonStringify(filterBody));
          }
          if (fieldName) {
            setSortColumn(fieldName);
            groupByEncounterRef.current = false;
          }
          let filterObj = {
            combinator: Object.keys(combinationFilters)?.length
              ? combinationFilters?.combinator ?? "AND"
              : filterBody?.combinator ?? "AND",
            filters: Object.keys(combinationFilters)?.length
              ? combinationFilters?.filters?.filter((el) => el !== null) ?? []
              : filterBody?.filters?.filters?.filter((el) => el !== null) ?? [],
          };

          if (!props.isQualityAssessment) {
            filterObj = {
              combinator: "AND",
              filters: [
                filterObj,
                {
                  key: "service",
                  operation: "eq",
                  value: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
                },
              ],
            };
          }
          const requestBody = {
            ...filterObj,
            pagination: {
              limit: newPageSize,
              offset: (pageNumber - 1) * PAGE_SIZE,
              sort: fieldName ?? activeView?.sort_order?.fieldName ?? DEFAULT_SORT_FIELD,
              order: order ?? activeView?.sort_order?.order ?? DEFAULT_SORT_ORDER,
            },
            groupByEncounter: groupByEncounterRef.current,
            groupByCQAWorkItemId: undefined,
          };

          if(props.groupByCQAWorkItemId){
            requestBody.groupByCQAWorkItemId = true;
          }

          if (requestBody?.pagination?.sort) {
            const modelName = allColumnDefs?.current?.find(col => requestBody?.pagination?.sort === col?.field)?.modelName;
            requestBody.pagination.sort = `${modelName ?? WidgetConstants?.WORKITEM}.${requestBody.pagination.sort}`
          }
          appendModelNameToFilterKey(requestBody.filters);
          setAllFilters(requestBody);
          const requestProjectIdentifier = currentProjectRef.current;
          if (props.shouldLoadData === false) {
            params.success({
              rowData: [],
              rowCount: 0,
            });
            return;
          }
          props?.client
            .mutate({
              mutation: SearchWorkItemEntries,
              context: {
                fetchOptions: {
                  signal: abortControllerRef.current.signal,
                },
              },
              variables: {
                DirectoryType: DirectoryType,
                request: requestBody,
              },
              fetchPolicy: "no-cache",
            })
            .then((response: any) => {
              if (requestProjectIdentifier !== currentProjectRef.current) {
                console.log('Discarding stale response for project:', requestProjectIdentifier);
                return null;
              }
              getBulkNotes(response?.data?.SearchWorkItems?.data || [], params);
              searchCount = response.data.SearchWorkItems?.total;
              setCountOfEntries(searchCount);
              setShowCount(true);
              const columnData = ColumDataCall(
                response.data.SearchWorkItems?.data
              );

              const lastRow = params.request.endRow >= searchCount ? searchCount : -1;
              setColumnData(columnData);
              params.success({
                rowData: columnData,
                rowCount: lastRow,
              });
              viewReset.current = false;
              return response;
            })
            // apply cqa data transformations
            .then((response: SearchWorkItemsResponse) => {
              if (!response) return;
              if (requestProjectIdentifier !== currentProjectRef.current) return;
              if(props.isQualityAssessment){
                cqaTransformWorkItems(response.data.SearchWorkItems.data)
                  .then((workItems) => {
                    // Final staleness check before updating UI
                    if (requestProjectIdentifier !== currentProjectRef.current) {
                      return;
                    }
                    const columnData = ColumDataCall(workItems);
                    setColumnData(columnData);
                    params.success({
                      rowData: columnData
                    })
                  });
              }
            })
            .catch((error: any) => {
              if (error.name === 'AbortError' || error.message?.includes('aborted')) {
                console.log('Request aborted - expected during project switching');
                return;
              }
              viewReset.current = false;
              params.fail();
              retryCount.current = retryCount.current + 1;
              if (retryCount.current <= 3) params.api.retryServerSideLoads();
              openSnackbar("Failed to Load Entries", "error");
            });
        }
      },
    };
  }

  const mapViewDataToViewType = (view): ViewType => {
    return {
      viewId: view?.data?.view_id,
      name: view?.data?.name,
      description: view?.data?.description,
      filters: view?.data?.config?.filters,
      userFilters: view?.data?.config?.userFilters,
      columns: buildColumnDefs(view?.data?.name, view?.data?.config?.columns) ?? [],
      sort_order: view?.data?.config?.sort_order,
      view_type: view?.data?.view_type ?? "global",
widgetType: view?.data?.widgetType,
      editable: view?.data?.editable,
createdby: view?.data?.createdby,
      userPreferences: view?.userPreferences ?? [],
      scope: view?.data?.scope,
    };
  };


  const buildColumnsDict = (detailGridOptions) => {
    let columnNames = [];
    detailGridOptions?.columnDefs?.map((columnDef) => {
      if (!columnDef?.cellRenderer) {
        columnNames.push(columnDef?.id);
      }
    });
    if (detailGridOptions?.masterDetail) {
      const l = buildColumnsDict(
        detailGridOptions?.detailCellRendererParams?.detailGridOptions
      );
      columnNames = [...columnNames, ...l];
    }
    return columnNames;
  };

  const firstRender = useRef(true);

  const getTotalCountAsString = () => {
    if (countOfEntries > 1) {
      return `Found ${countOfEntries} records`;
    } else {
      return `Found ${countOfEntries} record`;
    }
  };

  const onBodyScroll = () => {
  // when scroll, recompute again since new rows may0 be rendered
  recomputeRenderedSelectedRows();
};

const onSelectionChanged = () => {
  recomputeRenderedSelectedRows();
};

const recomputeRenderedSelectedRows = () => {
  if (!gridApi) return;

  const selectionState = gridApi.getServerSideSelectionState();
  const { selectAll, toggledNodes } = selectionState;

  const selectedRows: any[] = [];

  if (selectAll) {
    // User clicked "Select All"
    gridApi.forEachNode((node) => {
      if (node.isSelected()) {
        selectedRows.push(node.data);
      }
    });

    setAllSelected(true);
    setSelectedRows(selectedRows);
  } else {
    if (toggledNodes && toggledNodes.length > 0) {
      // few rows selected manually
      toggledNodes.forEach((nodeId) => {
        const node = gridApi.getRowNode(nodeId);
        if (node?.isSelected()) {
          selectedRows.push(node.data);
        }
      });
      setAllSelected(false);
      setSelectedRows(selectedRows);
    } else {
      // User unselected Select All (or cleared selection)
      setAllSelected(false);
      setSelectedRows([]);
    }
  }
};


  const refreshContent = () => {
    if (!gridApi) return;

    let finalFilter = undefined;
    const appliedFilters = buildSearchFilter(filters);
    const searchFilter = getSearchFilter();

    if (searchFilter !== undefined)
      finalFilter = {
        combinator: "AND",
        filters: appliedFilters?.filters?.length
          ? [searchFilter, appliedFilters]
          : [searchFilter],
      };
    else
      finalFilter = appliedFilters;

    gridApi?.setServerSideDatasource(
      updateServerSideDatasource(finalFilter, undefined, true)
    );
    if (gridApi) {
      try {
        gridApi.refreshCells();
      } catch {
          console.error('Grid may be destroyed or in invalid state (e.g. hidden container)');
      }
    }

    if (props.onTableRefresh) props.onTableRefresh();
  }

  const handleAfterExclusion = () => {
    gridApi.deselectAll();
    handleActionMenuClose();
    refreshContent();
    // event to trigger project counts refresh
    window.dispatchEvent(new CustomEvent('workitems-updated'));
  }

  const resetView = async () => {
    // Delete user view
    await props?.client
        .mutate({
          mutation: deleteUserView,
          variables: {
            viewId: activeView?.viewId,
            service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
          },
          fetchPolicy: "no-cache",
        })

    // Refresh views
    const updatedViewsResponse = await props?.client
        .query({
          query: getViews,
          variables: {
            widgetType: DirectoryType,
            service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name,
            showAvailableCases: coderAssignmentConfig?.some(
                (item) =>
                    item.keyname === "nightly_distribution" &&
                    item.values.some(
                        (valueObj) =>
                            valueObj.value === "false" &&
                            valueObj.serviceLines.includes(
                                reverseServiceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name
                            )
                    )
            ),
          },
          fetchPolicy: "no-cache",
        });

    const viewsArray = updatedViewsResponse?.data?.Views?.data;
    if (viewsArray?.length) {
      const views: ViewType[] = [
        ...viewsArray
          ?.map(mapViewDataToViewType)
          ?.filter((view) =>
            view?.name
            && view?.columns
            && view.widgetType === props.widgetType
          ),
      ];
      await updateViewsInCQA(views);
      setViews(views);
      handleViewChange(filterVisibleViews(views).find(view => view.viewId === activeView.viewId));
    }
  }

  const buildColumnDefs = (viewName, viewCols) => {
    const updatedViewCols = updatedWorklistColumnDefs(viewCols);
    updatedViewCols.forEach((col) => {
      col.resizable = true
    });

    let builtColumnDefs = [];

    // Add select operation to specific situations as it is not saved in the view //
  // Show "select all" column for all views (except Coding Quality views),
  // and also for CQA Manager views when the user has the CQA Manager role
  if (shouldShowSelectAll(viewName, roles)) {
      builtColumnDefs.push(selectColumnDef);
    } 
    if (roles.includes(USER_ROLES.PROFESSIONAL_SERVICES)) {
      builtColumnDefs.push(workitemDetailColumnDef);
    }

    // Add coder notes column //
    if (props.widgetType === "workitem")
      builtColumnDefs.push(createCoderNotesColumnDef(userContext?.serviceLine?.name));

    builtColumnDefs = builtColumnDefs.concat(updatedViewCols);

    if (userContext?.serviceLine?.name === 'Pathology' && builtColumnDefs?.findIndex(col => col.field === 'accession') === -1) {
      builtColumnDefs.unshift(accessionColumnDef);
    }

   return builtColumnDefs;
  };

  const handleSaveView = (viewConfig) => {

    saveView(
        { filters: viewConfig.filters, columns: viewConfig.columns,sort_order: viewConfig.sort_order },
        viewConfig.name,
        viewConfig.description,
        "SYSTEM",
        viewConfig.isGlobal ? "global" : "local",
        viewConfig.isEdit,
        viewConfig.viewId,
        roles,
        props.isQualityAssessment ? undefined : (serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name)
    );
  };

  useImperativeHandle(props?.childRef, () => ({
    refreshContent,
    getViews: () => views,
    getActiveView: () => activeView,
    handleViewChange,
    handleSaveView,
    refreshViews: fetchViews,
    getColumnOptions: () => (detailGridOptions as any)?.columnDefs || [],
    getAllCoders: () => allCoders,
    getAllGroups: () => allGroups,
    getQueues: () => queues,
    getDetailGridOptions: () => detailGridOptions,
    getOptionsDict: () => optionsDict,
    setOptionsDict: (value) => setOptionsDict(value),
  }), [refreshContent, views, activeView, handleViewChange, handleSaveView, fetchViews, detailGridOptions, allCoders, allGroups, queues]);
  //endregion

  //region Handlers
  const handleFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleAddView = (event) => {
    setAddViewAnchorEl(event.currentTarget);
  };

  const handleActionMenu = (event) => {
    setActionMenuAnchorEl(event.currentTarget);
  };

  const handleHideFields = (event) => {
    setHideFieldsAnchorEl(event.currentTarget);
  };

  const handleHideFieldsClose = () => {
    setHideFieldsAnchorEl(null);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleAddViewClose = () => {
    setAddViewAnchorEl(null);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
  };

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      if (nameSearch.length) {
        let combinationFilters: FiltersType = {};
        const appliedFilters = buildSearchFilter(filters);
        const searchFilter = getSearchFilter();
        combinationFilters = {
          combinator: "AND",
          filters: appliedFilters?.filters?.length
            ? [searchFilter, appliedFilters]
            : [searchFilter],
        };
        firstSearchRender.current = true;
        setShowCount(false);
        gridApi?.setServerSideDatasource(
          updateServerSideDatasource(combinationFilters, undefined, true)
        );
      } else {
        setShowCount(false);
        firstSearchRender.current = true;
        gridApi?.setServerSideDatasource(
          updateServerSideDatasource({}, undefined, true)
        );
      }
    }
  };

  const handleCloseSearch = () => {
    setNameSearch("");
    setShowCount(false);
    firstSearchRender.current = true;
    let body = {};
    if (filters?.length) {
      body = buildSearchFilter(filters);
    }
    gridApi?.setServerSideDatasource(
      updateServerSideDatasource(body, undefined, true)
    );
  };
  
  const handleGroupBy = () => {
    //  Clear selection state before refreshing
    if (!gridApi) return;
    
    gridApi.deselectAll();
    // to clear "select all" state 
    gridApi.setServerSideSelectionState({
      selectAll: false,
      toggledNodes: {}
    });

    if (sortColumn) {
      const cols = columnDefs?.map((column) => {
        return { ...column, sort: "" };
      });
      setColumnDefs(cols);
      setSortColumn("");
      groupByEncounterRef.current = true;
      gridApi.refreshServerSideStore({ purge: true });
    } else if (groupByEncounterRef.current) {
      groupByEncounterRef.current = false;
    } else {
      groupByEncounterRef.current = true;
    }
    let body = {};
    if (filters?.length) {
      body = buildSearchFilter(filters);
    }
    gridApi?.setServerSideDatasource(
      updateServerSideDatasource(body, undefined, true)
    );
  };

  const handleReassignCases = async (
    cases,
    assignee,
    is_manual_assignment: Boolean = false
  ) => {
    const numCases = cases?.length;

    try {
      if (props.isQualityAssessment) {
        // CQA Reassignment
        const reassignments = cases.map((caseItem) => ({
          assignee_id: assignee,
          sd_case_id: caseItem.case_id,
        }));

        await apiReassignCQAWorkitems(reassignments);

        openSnackbar(
          `${
            allSelected ? "All" : numCases.toString()
          } case(s) successfully reassigned.`,
          "success"
        );

        // Clear selections and refresh
        gridApi.deselectAll();
        handleActionMenuClose();
        refreshContent();

        // Trigger project counts refresh
        window.dispatchEvent(new CustomEvent("workitems-updated"));
      } else {
        // Regular Worklist Reassignment
        const caseIds = cases?.map((caseObj) => caseObj?.case_id);
        const assigneeDetails = availableCoders.find(
          (coder) => coder?.data?.worker_id == assignee
        );

        const requestObject =
          cases?.map(
            ({
              case_id,
              username,
              createdat,
              automate_status,
              cmx_process_id,
              subtenant_id,
              health_system_id,
            }) => ({
              owner_first_name: assigneeDetails?.data?.first_name,
              owner_last_name: assigneeDetails?.data?.last_name,
              assignee_first_name: assigneeDetails?.data?.first_name,
              assignee_last_name: assigneeDetails?.data?.last_name,
              process_instance_guid: cmx_process_id,
              work_item_id: case_id,
              tenant_uid: subtenant_id,
              health_system_tenant_uid: health_system_id,
              work_item_created_timestamp: createdat,
              owner_username: assigneeDetails?.data?.username,
              owner_worker_id: assigneeDetails?.data?.worker_id,
              owner_user_id: assigneeDetails?.data?.user_id,
              owner_updated_timestamp: new Date().toISOString(),
              assignee_username: assigneeDetails?.data?.username,
              assignee_updated_timestamp: new Date().toISOString(),
              assignee_user_id: assigneeDetails?.data?.user_id,
              assignee_worker_id: assigneeDetails?.data?.worker_id,
            })
          ) ?? [];

        const ownerAssignedAt = new Date().toISOString();
        const reqData: object = {
          assignee,
          owner_assigned_at: ownerAssignedAt,
        };

        if (["On Hold", "Current", "My Current", "My Holds"]?.includes(activeView?.name)) {
          reqData["owner"] = assignee;
        }

        await props?.client.mutate({
          mutation: BulkUpdateWorkItems,
          variables: {
            request: {
              filter: caseIds,
              entryData: reqData,
              caseDetails: requestObject,
              is_manual_assignment: is_manual_assignment,
            },
          },
          fetchPolicy: "no-cache",
        });

        openSnackbar(
          `${
            allSelected ? "All" : numCases.toString()
          } cases were successfully moved.`,
          "success"
        );

        gridApi.deselectAll();
        handleActionMenuClose();
      }
    } catch (error) {
      console.error("Error reassigning cases:", error);
      openSnackbar(
        `Failed to ${props.isQualityAssessment ? "reassign" : "move"} cases.`,
        "error"
      );
    }
  };

  
  const handleRenameColumnName = (id, newValue) => {
    props?.client
      .mutate({
        mutation: UpdateColumnName,
        variables: {
          AttributeId: id,
          request: {
            label: newValue,
          },
        },
        fetchPolicy: "no-cache",
      })
      .then((response: any) => {
        var defs = [];
        columnDefs?.map((def) => {
          if (def?.id === id) {
            defs.push({ ...def, headerName: newValue });
          } else {
            defs.push(def);
          }
        });
        setColumnDefs(defs);
      })
      .catch((error: any) => {
        console.log(error);
      });
  };

  const handleColumnMovedDebounced = debounce(handleColumnMoved, 1000);

  function handleColumnMoved(event) {
    if (event?.source === "uiColumnMoved") {
      const newColDefs = [...columnDefs];
      const updatedColumnDefs = [...event?.columnApi?.columnModel?.columnDefs];
      const currentIndex = newColDefs?.findIndex(
        (coldef) => coldef?.field === event.column?.colId
      );
      // Remove the column from its current position
      const [movedColumn] = updatedColumnDefs.splice(currentIndex, 1);
      // Insert the column into its new position
      updatedColumnDefs.splice(event.toIndex, 0, movedColumn);
      // Update the state with the new column order
      setColumnDefs(updatedColumnDefs);
      const tempViews: ViewType[] = JSON.parse(JSON.stringify(views));
      const viewIdx = tempViews?.findIndex(
        (view) => view?.viewId === activeView?.viewId
      );
      tempViews[viewIdx] = { ...tempViews[viewIdx], columns: updatedColumnDefs };

      setViews(tempViews);

      saveUserPreferences({
        columns: updatedColumnDefs,
        filters: activeView?.filters,
        userFilters: activeView?.userFilters,
      });
    }
  }

  const handleColumnResizedDebounced = debounce(handleColumnResized, 1000);

  function handleColumnResized(event) {
    const newColDefs = [...columnDefs];
    const updatedColumnDefs = [...event?.columnApi?.columnModel?.columnDefs];

    event.columns.forEach((column) => {
      if (defaultColumns.includes(column.colDef.id)) return;
      const updatedColWidth = column.actualWidth;
      const updatedColIdx = newColDefs?.findIndex(
        (coldef) => coldef.id === column.colDef.id
      );

      updatedColumnDefs[updatedColIdx].width = updatedColWidth;
    })
    setColumnDefs(updatedColumnDefs);

    const tempViews: ViewType[] = [...views];
    const viewIdx = tempViews?.findIndex(
      (view) => view?.viewId === activeView?.viewId
    );

    tempViews[viewIdx] = {
      ...tempViews[viewIdx],
      columns: updatedColumnDefs,
    };

    setViews(tempViews);
    saveUserPreferences({
      columns: updatedColumnDefs,
      filters: activeView?.filters,
      userFilters: activeView?.userFilters,
      sort_order: activeView?.sort_order
    });
  }

  const refreshCache = async (caseId) => {
    try {
      const refreshPayload = {
        query: allFilters,
        case_id: caseId
    };
      await props?.client.mutate({
        mutation: RefreshCaseCache,
        variables: {
          refreshPayload
        },
        fetchPolicy: "no-cache"
      });
    } catch (error) {
      console.error('Error refreshing cache:', error);
    }
  };
  //endregion

  //region Lifecycle Methods
  useEffect(() => {
    if (columnDefs.length !== 0) {
      let hidden = 0;
      columnDefs?.map((column) => {
        if (column?.hide) {
          hidden += 1;
        }
      });
      setHiddenNumberOfFields(hidden);
    }
  }, [columnDefs]);

  useEffect(() => {
    if (activeView) {
      viewReset.current = true;
      firstSearchRender.current = true;
      firstUpdateRender.current = true;
      // Rebuild columns to ensure metadata columns are fresh with proper cellRenderers
      const nonMetaColumns = activeView?.columns?.filter(col => !defaultColumns.includes(col.id));
      const rebuiltColumns = buildColumnDefs(activeView?.name, nonMetaColumns);
      setColumnDefs(rebuiltColumns);
      const body = buildSearchFilter(activeView?.userFilters);
      gridApi?.setServerSideDatasource(
        updateServerSideDatasource(body, true, true)
      );
    }
  }, [activeView, activeSubviewId]);

  useEffect(() => {
    if (columnDefs.length !== 0) {
      const cols = columnDefs?.map((column) => {
        if (column.field === sortColumn) {
          const { sort, ...rest } = column;
          return rest;
        }
        return column;
      });
      setColumnDefs(cols);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortColumn]);

  useEffect(() => {
    getCoders();
    getAllGroups();
    getQueues();
    checkUserGroups();
  }, []);

  // Re-check groups when parent toggles loading state
  useEffect(() => {
    checkUserGroups();
  }, [props.shouldLoadData]);

  useEffect(() => {
    // Update project ref and abort any in-flight requests on project change
    currentProjectRef.current = props.projectIdentifier;
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    // Clear selection state when navigating between projects/worklists
    if (showManagerEmptyState) {
      setGridApi(null);
      setAllSelected(false);
      setSelectedRows([]);
      return;
    }

    // Clear grid data before refreshing
    setColumnData([]);
    setCountOfEntries(0);
    setShowCount(false);

    if (gridApi) {
      // Clear "select all" state first
      gridApi.setServerSideSelectionState({
        selectAll: false,
        toggledNodes: {}
      });
      // Then deselect all nodes
      gridApi.deselectAll();
      // Reset local selection state
      setAllSelected(false);
      setSelectedRows([]);

      // Update datasource after clearing selection
      gridApi.setServerSideDatasource(
        updateServerSideDatasource([], true, true)
      );
    } else {
      setAllSelected(false);
      setSelectedRows([]);
    }
  }, [props.overridingViewFilters, props.projectIdentifier, showManagerEmptyState]);
  //endregion

  useEffect(() => {
    if (worklistConfig && Object.keys(worklistConfig).length > 0) {
      console.log('🎯 Worklist configuration loaded:', worklistConfig);

      // Set search options from configuration
      if (worklistConfig.availableOptions) {
        setSearchOptions(worklistConfig.availableOptions);
      }

      // Set default search type
      if (worklistConfig.defaultFilterOption && !searchType) {
        setSearchType(worklistConfig.defaultFilterOption);
      }
    }
  }, [worklistConfig, searchType]);

  return (
    <>
      <Box sx={{width: '100%', margin: 0, padding: 0}}>
        <Grid
          container
          marginBottom={"20px"}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid
            item
            container
            xs
            alignItems="center"
          >
            {props.widgetHeader}
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
            >
              <Grid item>
                {typeof props.widgetTitle === 'string' ? (
                    <Typography variant="h2">
                      {props.widgetTitle}
                    </Typography>
                ) : (
                    props.widgetTitle
                )}
              </Grid>

              <Grid item>
                {props.widgetTitleActions}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {props.GraderCountsRow}
        <Box
          sx={{
            boxShadow:
              "0px 4px 8px 3px rgba(0, 0, 0, 0.15), 0px 1px 3px rgba(0, 0, 0, 0.3)",
          }}
        >
          {/*Top Bar*/}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              padding: "12px",
              backgroundColor: "white",
            }}
          >            
            {/* QA Status Tabs */}
            {shouldShowStatusTabs && (
              <StatusTabs
                statuses={availableStatusesForTabs}
                selectedStatus={props.selectedStatus ?? null}
                onStatusChange={props.onStatusChange || (() => {})}
                selectedProject={props.selectedProject}
                countIsLoading={props.countIsLoading}
                labelOverrides={statusTabsLabelOverrides}
              />
            )}
            {showManagerEmptyState && emptyStateContent ? (
              <Box
                sx={{
                  minHeight: "320px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  padding: "60px 40px",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: theme.palette.artisticTextHigh.main,
                    fontWeight: 600,
                    marginBottom: "12px",
                  }}
                >
                  {emptyStateContent.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: "640px",
                    color: theme.palette.artisticTextMedium.main,
                  }}
                >
                  {emptyStateContent.description}
                </Typography>
              </Box>
            ) : (
              <>
                {/*View Tabs*/}            
                {
                  !props.isQualityAssessment && !props.hideTabNavigation  ? (
                    <Box sx={{ marginBottom: "20px" }}>
                      <TabContext value={activeView?.name}>
                        <Box
                          sx={{
                            display: "flex",
                            borderBottom: "1px solid lightgray",
                          }}
                        >
                          <TabList
                            sx={{
                              "& .Mui-selected": {
                                color: theme.palette.artisticTextInteractive.main,
                              },
                            }}
                            variant="scrollable"
                          >
                            {filterVisibleViews(views)?.map((tab) => (
                              <Tab
                                style={{ minWidth: "76px", padding: "6px 32px" }}
                                label={
                                  <Typography variant="body1">{tab.name}</Typography>
                                }
                                value={tab.name}
                                onClick={() => {
                                  handleViewChange(tab);
                                }}
                              />
                            ))}
                          </TabList>
                          <Button
                            onClick={handleAddView}
                            disableRipple={true}
                            sx={{ height: "48px" }}
                          >
                            <IconButton disableRipple={true}>
                              <img src={add} alt="add" style={{ height: "14px" }} />
                            </IconButton>
                            Manage Views
                          </Button>
                          <div className="addViewPopup">
                            <caseContext.Provider
                              value={{
                                directoryType: DirectoryType,
                                columnOptions: columnDefs,
                                detailGridOptions,
                                optionsDict: optionsDict,
                                setOptionsDict: setOptionsDict,
                              }}
                            >
                              <AddViewPopup
                                client={props?.client}
                                filters={filters}
                                directoryType={DirectoryType}
                                widgetType={props.widgetType}
                                setFilters={setFilters}
                                anchorEl={addviewAnchorEl}
                                handleClose={handleAddViewClose}
                                handleSaveView={handleSaveView}
                                onHide={hideColumns}
                                onSelect={hideColumns}
                                options={(detailGridOptions as any)?.columnDefs}
                                views={views}
                                roles={roles}
                                allCoders={allCoders}
                                allGroups={allGroups}
                                queues={queues}
                                refreshViews={fetchViews}
                              />
                            </caseContext.Provider>
                          </div>
                        </Box>
                      </TabContext>
                    </Box>
                  ) : null
                }
                {/* Subview tabs for queue views */}
                {props.hideTabNavigation && isQueueView(activeView) ? (
                  <TabContext value={activeSubviewId}>
                    <Box
                      sx={{
                        display: "flex",
                        borderBottom: "1px solid lightgray",
                      }}
                    >
                      <TabList>
                        {QUEUE_SUBVIEW_CONFIG.map((subview) => (
                          <Tab
                            key={subview.id}
                            label={subview.label}
                            value={subview.id}
                            onClick={() => handleSubviewChange(subview.id)}
                          />
                        ))}
                      </TabList>
                    </Box>
                  </TabContext>
                ) : null}

                {/*Grid Filters*/}
                <Box sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: "15px",
                  width: "100%",
                }}>
              {/*Search Bar*/}
              <Box>
                {searchOptions.length > 0 && (
                        <SmartSearchField
                            searchOptions={searchOptions}
                            defaultOption={worklistConfig?.defaultFilterOption || 'patient_mrn'}
                            onSearch={handleSmartSearch}
                            onClear={handleSmartSearchClear}
                            placeholder={`Search by ${worklistConfig?.availableOptions?.find(opt => opt.key === worklistConfig?.defaultFilterOption)?.label || 'patient MRN'}`}
                            value={searchValue}
                            width="14vw"
                        />)}
              </Box>

              {/*Hidden Fields*/}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Badge
                  badgeContent={hiddenNumberOfFields}
                  color="success"
                  invisible={hiddenNumberOfFields ? false : true}
                >
                  <Button
                    onClick={handleHideFields}
                    disableRipple={true}
                    sx={{
                      gap: "5px",
                      backgroundColor: hiddenNumberOfFields
                        ? "#e1e3e1"
                        : "unset",
                      "&:hover": {
                        backgroundColor: hiddenNumberOfFields
                          ? "#cfd1cf"
                          : "auto",
                      },
                    }}
                  >
                    <img src={hidden} alt="hidden" className="hidden-image" />
                    <Typography
                      variant="subtitle1"
                      color="artisticTextMedium.main"
                      fontWeight={400}
                    >
                      Hide Fields
                    </Typography>
                  </Button>
                </Badge>

                <OptionsMenu
                  anchorEl={hideFieldsAnchorEl}
                  handleClose={handleHideFieldsClose}
                  onHide={hideColumns}
                  onSelect={hideColumns}
                  options={columnDefs}
                  handleRenameColumnName={handleRenameColumnName}
                  setOptions={setColumnDefs}
                  handleHideAll={hideAllFields}
                  handleShowAll={showAllFields}
                />
              </Box>

              {/*Filters*/}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Badge
                  badgeContent={numberOfFilters}
                  color="success"
                  invisible={numberOfFilters === 0}
                >
                  <Button
                    onClick={handleFilter}
                    disableRipple={true}
                    sx={{
                      gap: "5px",
                      paddingY: "8px",
                      backgroundColor: filterApplied ? "#e1e3e1" : "unset",
                      "&:hover": {
                        backgroundColor: filterApplied ? "#cfd1cf" : "auto",
                      },
                    }}
                  >
                    <img src={filter} alt="search" className="filter-image" />
                    <Typography
                      variant="subtitle1"
                      color="artisticTextMedium.main"
                      fontWeight={400}
                    >
                      Filter
                    </Typography>
                  </Button>
                </Badge>
                <Popover
                  open={Boolean(filterAnchorEl)}
                  onClose={handleFilterClose}
                  anchorEl={filterAnchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <div style={{ padding: "20px" }}>
                    <Box>
                      <Typography variant="h6" sx={{ mb: "10px" }}>
                        Filters
                      </Typography>
                      <Divider
                        sx={{
                          bgcolor: theme.palette.artisticStroke100.main,
                          mb: "5px",
                        }}
                      />
                    </Box>

                    <Filters
                      isEditable={true}
                      client={props?.client}

                      directoryType={DirectoryType}
                      detailGridOptions={detailGridOptions}

                      // @ts-ignore
                      columnOptions={(detailGridOptions?.columnDefs ?? []).filter((col) => !defaultColumns.includes(col.id))}
                      optionsDict={optionsDict}
                      setOptionsDict={setOptionsDict}

                      filters={filters}
                      setFilters={setFilters}

                      applyFilters={applyFilters}
                      removeFilters={removeFilters}

                      allCoders={allCoders}
                      allGroups={allGroups}
                      queues={queues}

                      hasBorder={false}
                    />
                  </div>
                </Popover>
              </Box>

              {/*Group By Encounter*/}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >

                {!props.isQualityAssessment && (<Button
                  onClick={handleGroupBy}
                  disableRipple={true}
                  className="reset-text"
                  data-testid="group-by-button"
                  sx={{
                    gap: "5px",
                    backgroundColor: groupByEncounterRef.current
                      ? "#EDF4F7"
                      : "unset",
                  }}
                >
                  {sortColumn && (
                    <img src={reset} alt="reset" className="filter-image" />
                  )}
                  {groupByEncounterRef.current && (
                    <img src={check} alt="check" className="filter-image" />
                  )}
                  <Typography
                    variant="subtitle2"
                    color="#203D48"
                    fontWeight={400}
                  >
                    {sortColumn ? (
                      RESET_DEFAULT_SORT
                    ) : (
                      <>
                        {groupByEncounterRef.current
                          ? GROUPED_BY_ENCOUNTER
                          : NOT_GROUPED_BY_ENCOUNTER}
                      </>
                    )}
                  </Typography>
                </Button>
                )}
                {sortColumn && (
                  <Tooltip
                    title={
                      "Reset your view to a sort by date to enable grouping cases by encounter."
                    }
                  >
                    <img src={info} alt="info" className="info" />
                  </Tooltip>
                )}
              </Box>

              {/*Select Toggle*/}
                {shouldShowSelectAll(activeView?.name, roles) && ( 
               <Box
                  sx={{
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <Button
                    disableRipple={true}
                    className="select-toggle"
                    sx={{
                      gap: "5px",
                      backgroundColor: selectedRows.length === countOfEntries
                        ? "#EDF4F7"
                        : "unset",
                      border: selectedRows.length !== 0 && selectedRows.length < countOfEntries
                        ? "1px solid #A9D5E5"
                        : "unset"
                    }}>
                    <Typography
                      variant="subtitle2"
                      color="#203D48"
                      fontWeight={400}
                    >
                      {`${selectedRows.length} selected`}
                    </Typography>
                  </Button>
                </Box>
              )}

              {/*Refresh*/}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Button
                  onClick={() => refreshContent()}
                  style={{ gap: "5px" }}
                >
                  <img src={refresh} alt="refresh" />

                  <Typography
                    variant="subtitle1"
                    color="artisticTextMedium.main"
                    fontWeight={400}
                  >
                    Refresh
                  </Typography>
                </Button>
                <Button
                  onClick={() => resetView()}
                  style={{ gap: "5px" }}
                >
                  <img src={resetViewIcon} alt="reset view" width={"20px"} height={"20px"} />

                  <Typography
                    variant="subtitle1"
                    color="artisticTextMedium.main"
                    fontWeight={400}
                  >
                    Reset View
                  </Typography>
                </Button>
              </Box>

              {/*Manager Action Menu*/}
                <>
                  {/* Add action menu - show for workitem always, for CQA only if user is CQA Manager */}
                  {(
                      props.widgetType === "workitem" || (props.widgetType === WidgetConstants.CQA && roles?.includes("cqa_manager"))
                  ) && (
                    <>
                      <Button onClick={handleActionMenu} disableRipple={true} sx={{ minWidth: 0 }}>
                        <img
                          src={verticalMenu}
                          alt="actions"
                          className="action-dots"
                        />
                      </Button>
                      <ActionMenu
                        client={props?.client}
                        selected={selectedRows}
                        anchorEl={actionMenuAnchorEl}
                        handleMove={handleReassignCases}
                        coders={activeView?.name === 'QA' ?
                          availableCoders.filter(coder => coder?.data?.roles?.every(role => role.name === 'coding_manager'))
                          : availableCoders}
                        handleClose={handleActionMenuClose}
                        allSelected={allSelected}
                        isChangeOwner={["On Hold", "Current","My Current","My Holds"]?.includes(activeView?.name)}
                        activeView={activeView?.name}
                        handleAfterExclusion={handleAfterExclusion}
                        serviceLine={serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name}
                        isQualityAssessment={props.isQualityAssessment}
                        isCQAManager={roles?.includes("cqa_manager")}
                        handleAfterRevert={handleAfterExclusion}
                        cqaWorkitemStatus={props.cqaWorkitemStatus}
                      />
                    </>
                  )}
                </>
              
              {/*Record Count*/}
              <Box
                style={{
                  display: "flex",
                  alignSelf: "center",
                  flexGrow: 1,
                  justifyContent: "flex-end",
                }}
              >
                {showCount && (
                  <Typography
                    sx={{
                      display: "flex",
                      justifySelf: "flex-end",
                      alignSelf: "flex-end",
                      color: theme.palette.artisticTextMedium.main,
                    }}
                  >
                    {getTotalCountAsString()}
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}
          </Box>

          {!showManagerEmptyState && (
            <>
              {/*Grid*/}
              {projectRetrievalError ? (
                <Box
                  sx={{
                    height: "72vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "white",
                    padding: "40px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.artisticTextMedium.main,
                      textAlign: "center",
                      maxWidth: "600px",
                    }}
                  >
                    There was an error retrieving projects for this user
                  </Typography>
                </Box>
              ) : (groupsChecked && !userHasGroups) ? (
                <Box
                  sx={{
                    height: "72vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "white",
                    padding: "40px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.artisticTextMedium.main,
                      textAlign: "center",
                      maxWidth: "600px",
                    }}
                  >
                    User is not a part of any CQA groups please contact administrator
                  </Typography>
                </Box>
              ) : props.shouldLoadData === false ? (
                <div
                  className="ag-theme-alpine"
                  data-testid="ag-grid-parent"
                  style={{
                    height: "72vh",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    backgroundColor: "white",
                  }}
                >
                  <ReactGrid
                    keepDetailRows={true}
                    detailCellRendererParams={detailCellRendererParams}
                    suppressColumnVirtualisation={true}
                    masterDetail={true}
                    rowModelType={"serverSide"}
                    onGridReady={onGridReady}
                    onGridPreDestroyed ={onGridPreDestroyed}
                    columnDefs={columnDefs}
                    enableRangeSelection={true}
                    enableCharts={true}
                    detailRowAutoHeight={true}
                    cacheBlockSize={PAGE_SIZE}
                    gridOptions={{}}
                    rowSelection={"multiple"}
                    onSelectionChanged={onSelectionChanged}
                    onBodyScroll={onBodyScroll}
                    suppressRowClickSelection={true}
                    onCellClicked={(params) => {}}
                    onColumnMoved={handleColumnMovedDebounced}
                    onColumnResized={handleColumnResizedDebounced}
                  />
                </div>
              ) : (
              <div
                className="ag-theme-alpine"
                data-testid="ag-grid-parent"
                style={{
                  height: "72vh",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  backgroundColor: "white",
                }}
              >
                <ReactGrid
                  keepDetailRows={true}
                  detailCellRendererParams={detailCellRendererParams}
                  suppressColumnVirtualisation={true}
                  masterDetail={true}
                  rowModelType={"serverSide"}
                  onGridReady={onGridReady}
                  onGridPreDestroyed ={onGridPreDestroyed }
                  columnDefs={columnDefs}
                  enableRangeSelection={true}
                  enableCharts={true}
                  detailRowAutoHeight={true}
                  cacheBlockSize={PAGE_SIZE}
                  gridOptions={{}}
                  rowSelection={"multiple"}
                  onSelectionChanged={onSelectionChanged}
                  onBodyScroll={onBodyScroll}
                  suppressRowClickSelection={true}
                  onCellClicked={(params) => {
                    if(params?.colDef?.field === "workitem_detail") {
                      props.handleViewWorkitemData(params?.data);
                    } else {
                        handleRowClick(params?.data, {
                          query: allFilters,
                          resultSet: generateKey(allFilters)
                        });
                        refreshCache(params?.data?.case_id);
                    }
                  }}
                  onColumnMoved={handleColumnMovedDebounced}
                  onColumnResized={handleColumnResizedDebounced}
                />
              </div>
               )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default WorkItemTable;
